import React, { useState, useEffect, useRef } from 'react';
import { Card, Table, Button, Space, Modal, Input, Tabs, message, Tag, Statistic, Row, Col } from 'antd';
import { PlusOutlined, DeleteOutlined, ReloadOutlined, StockOutlined, ArrowUpOutlined, ArrowDownOutlined, MinusCircleOutlined, BarChartOutlined, SearchOutlined } from '@ant-design/icons';
import ReactEChartsCore from 'echarts-for-react';
import { watchlistApi, tradingApi } from '../services/api';

export default function WatchlistPage() {
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addModal, setAddModal] = useState({ open: false, code: '', name: '' });
  const [syncing, setSyncing] = useState(false);
  const [klineModal, setKlineModal] = useState({ open: false, stockCode: '', stockName: '', data: [], loading: false });
  const [search, setSearch] = useState('');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await watchlistApi.getAll();
      if (res.success) setStocks(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleAdd = async () => {
    if (!addModal.code) { message.warning('请输入股票代码'); return; }
    try {
      const res = await watchlistApi.add(addModal.code, addModal.name);
      if (res.success) {
        message.success('添加成功');
        setAddModal({ open: false, code: '', name: '' });
        loadData();
      } else {
        message.error(res.message);
      }
    } catch (e) { message.error('添加失败'); }
  };

  const handleDelete = async (id) => {
    try {
      const res = await watchlistApi.remove(id);
      if (res.success) { message.success('已删除'); loadData(); }
    } catch { message.error('删除失败'); }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await watchlistApi.sync();
      if (res.success) {
        message.success(`同步完成: ${res.data.success}/${res.data.total}`);
        loadData();
      }
    } catch { message.error('同步失败'); }
    finally { setSyncing(false); }
  };

  const scaleOptions = [
    { label: '5分', value: 5 },
    { label: '15分', value: 15 },
    { label: '30分', value: 30 },
    { label: '日线', value: 60 },
  ];
  const [klineScale, setKlineScale] = useState(60);
  const [showMacd, setShowMacd] = useState(false);

  // MACD 计算
  function calcEMA(data, period) {
    const k = 2 / (period + 1);
    const result = [data[0]];
    for (let i = 1; i < data.length; i++) {
      result.push(data[i] * k + result[i - 1] * (1 - k));
    }
    return result;
  }
  function calcMACD(closes) {
    const ema12 = calcEMA(closes, 12);
    const ema26 = calcEMA(closes, 26);
    const dif = ema12.map((v, i) => v - ema26[i]);
    const dea = calcEMA(dif, 9);
    const bar = dif.map((v, i) => 2 * (v - dea[i]));
    return { dif, dea, bar };
  }

  const openKLine = async (stockCode, stockName, scale) => {
    const s = scale || klineScale;
    setKlineScale(s);
    setKlineModal({ open: true, stockCode, stockName, data: [], loading: true });
    try {
      const res = await tradingApi.getKLine(stockCode, 200, s);
      if (res.success) {
        setKlineModal(prev => ({ ...prev, data: res.data || [], loading: false }));
      } else {
        message.error(res.message);
        setKlineModal(prev => ({ ...prev, loading: false }));
      }
    } catch (e) {
      message.error('获取K线失败: ' + (e.response?.data?.message || e.message));
      setKlineModal(prev => ({ ...prev, loading: false }));
    }
  };

  const klineOption = (() => {
    const macdCloses = klineModal.data.map(d => d.close_price);
    const macd = showMacd && macdCloses.length > 33 ? calcMACD(macdCloses) : null;

    const xData = klineModal.data.map(d => {
      const date = d.date || '';
      return date.length > 10 ? date.substring(5, 16) : date.substring(5);
    });
    const xDataFull = klineModal.data.map(d => d.date || '');

    // 动态构建 grid
    const grid = [
      { left: '8%', right: '8%', top: '3%', height: '47%' },
      { left: '8%', right: '8%', top: '54%', height: '18%' },
    ];
    if (macd) {
      grid.push({ left: '8%', right: '8%', top: '76%', height: '20%' });
    }

    // xAxis
    const xAxis = [
      { type: 'category', data: xData, gridIndex: 0,
        axisLabel: { fontWeight: 'bold', fontSize: 11, color: '#aaa', rotate: 0, interval: Math.max(Math.floor(xData.length / 8), 1) },
        axisLine: { lineStyle: { color: '#444' } }, splitLine: { show: false },
      },
      { type: 'category', data: xDataFull, gridIndex: 1,
        axisLabel: { show: false }, axisLine: { show: false }, splitLine: { show: false },
      },
    ];
    if (macd) {
      xAxis.push({ type: 'category', data: xDataFull, gridIndex: 2,
        axisLabel: { show: false }, axisLine: { show: false }, splitLine: { show: false },
      });
    }

    // yAxis
    const yAxis = [
      { type: 'value', scale: true, splitNumber: 6, gridIndex: 0,
        axisLabel: { color: '#888' }, axisLine: { lineStyle: { color: '#333' } },
        splitLine: { lineStyle: { color: '#2a2a4a' } },
      },
      { type: 'value', scale: true, splitNumber: 3, gridIndex: 1,
        axisLabel: { color: '#888', formatter: (v) => (v / 10000).toFixed(0) + '万' },
        axisLine: { lineStyle: { color: '#333' } }, splitLine: { show: false },
      },
    ];
    if (macd) {
      yAxis.push({ type: 'value', scale: true, splitNumber: 3, gridIndex: 2,
        axisLabel: { color: '#888' }, axisLine: { lineStyle: { color: '#333' } },
        splitLine: { show: false },
      });
    }

    // series
    const series = [
      { name: 'K线', type: 'candlestick', xAxisIndex: 0, yAxisIndex: 0,
        data: klineModal.data.map(d => [d.open_price, d.close_price, d.low_price, d.high_price]),
        itemStyle: { color: '#e74c3c', color0: '#27ae60', borderColor: '#e74c3c', borderColor0: '#27ae60' },
      },
      { name: '成交量', type: 'bar', xAxisIndex: 1, yAxisIndex: 1,
        data: klineModal.data.map(d => d.volume),
        itemStyle: { color: (p) => {
          const curr = klineModal.data[p.dataIndex];
          const prev = klineModal.data[p.dataIndex - 1];
          if (!curr || !prev) return '#aaa';
          return curr.close_price >= prev.close_price ? '#e74c3c' : '#27ae60';
        }},
      },
    ];

    if (macd) {
      series.push(
        { name: 'MACD_DIF', type: 'line', xAxisIndex: 2, yAxisIndex: 2,
          data: macd.dif, symbol: 'none', lineStyle: { width: 1, color: '#e6a817' } },
        { name: 'MACD_DEA', type: 'line', xAxisIndex: 2, yAxisIndex: 2,
          data: macd.dea, symbol: 'none', lineStyle: { width: 1, color: '#e67e22' } },
        { name: 'MACD_BAR', type: 'bar', xAxisIndex: 2, yAxisIndex: 2,
          data: macd.bar,
          itemStyle: { color: (p) => p.value >= 0 ? '#e74c3c' : '#27ae60' },
        },
      );
    }

    return {
      backgroundColor: '#1a1a2e',
      tooltip: {
        trigger: 'axis', axisPointer: { type: 'cross' },
        backgroundColor: 'rgba(30,30,60,0.9)', borderColor: '#333',
        textStyle: { color: '#eee' },
        formatter: (params) => {
          const candle = params.find(p => p.seriesName === 'K线');
          if (!candle) return '';
          const d = candle.data;
          const vol = params.find(p => p.seriesName === '成交量');
          const dif = params.find(p => p.seriesName === 'MACD_DIF');
          let html = `${candle.axisValue}<br/>开: ${d[0].toFixed(2)}  收: ${d[1].toFixed(2)}<br/>高: ${d[3].toFixed(2)}  低: ${d[2].toFixed(2)}`;
          if (vol) html += `<br/>量: ${(vol.data / 10000).toFixed(0)}万`;
          if (dif) {
            const dea = params.find(p => p.seriesName === 'MACD_DEA');
            const bar = params.find(p => p.seriesName === 'MACD_BAR');
            html += `<br/>DIF: ${dif.data?.toFixed(4)}  DEA: ${dea?.data?.toFixed(4)}<br/>BAR: ${bar?.data?.toFixed(4)}`;
          }
          return html;
        },
      },
      grid, xAxis, yAxis,
      dataZoom: [{ type: 'inside', start: 0, end: 100 }],
      series,
    };
  })();

  const columns = [
    { title: '股票代码', dataIndex: 'stock_code', width: 110, render: v => <code>{v}</code> },
    { title: '名称', dataIndex: 'stock_name', width: 120 },
    { title: '最新价', dataIndex: 'current_price', width: 100, sorter: (a, b) => a.current_price - b.current_price, render: (v, r) => (
      <span style={{ fontWeight: 600, color: r.change_percent >= 0 ? '#e74c3c' : '#27ae60' }}>
        {v > 0 ? v.toFixed(2) : '-'}
      </span>
    )},
    { title: '涨跌幅', dataIndex: 'change_percent', width: 90, defaultSortOrder: 'descend', sorter: (a, b) => a.change_percent - b.change_percent, render: (v) => {
      if (!v && v !== 0) return '-';
      return (
        <Tag color={v >= 0 ? 'red' : 'green'} style={{ fontWeight: 600 }}>
          {v >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
          {' '}{v >= 0 ? '+' : ''}{v.toFixed(2)}%
        </Tag>
      );
    }},
    { title: '昨收', dataIndex: 'pre_close', width: 90, render: v => v > 0 ? v.toFixed(2) : '-' },
    { title: '今开', dataIndex: 'open_price', width: 90, render: v => v > 0 ? v.toFixed(2) : '-' },
    { title: '最高', dataIndex: 'high_price', width: 90, render: v => v > 0 ? v.toFixed(2) : '-' },
    { title: '最低', dataIndex: 'low_price', width: 90, render: v => v > 0 ? v.toFixed(2) : '-' },
    { title: '成交量', dataIndex: 'volume', width: 100, render: v => v > 0 ? v.toLocaleString() : '-' },
    { title: '操作', width: 110, render: (_, r) => (
      <Space size={4}>
        <Button size="small" icon={<BarChartOutlined />} onClick={() => openKLine(r.stock_code, r.stock_name, klineScale)}>K线</Button>
        <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(r.id)} />
      </Space>
    )},
  ];

  const now = new Date();
  const hour = now.getHours(), min = now.getMinutes();
  const timeNum = hour * 100 + min;
  const amOpen = timeNum >= 930 && timeNum <= 1130;
  const pmOpen = timeNum >= 1300 && timeNum <= 1500;
  const isTrading = (hour >= 9 && hour < 15) && !(timeNum > 1130 && timeNum < 1300);
  const tradeStatus = isTrading ? (amOpen ? '上午交易中' : '下午交易中') : '休市中';
  const startTrade = isTrading ? (amOpen ? '09:30' : '13:00') : '--:--';
  const endTrade = isTrading ? (amOpen ? '11:30' : '15:00') : '--:--';

  const gainers = [...stocks].filter(s => s.change_percent > 0).length;
  const losers = [...stocks].filter(s => s.change_percent < 0).length;

  return (
    <div>
      <Row gutter={[16, 16]}>
        <Col span={6}><Card><Statistic title="自选股" value={stocks.length} suffix="只" prefix={<StockOutlined />} valueStyle={{ color: '#2980b9' }} /></Card></Col>
        <Col span={6}><Card><Statistic title="上涨" value={gainers} suffix="只" valueStyle={{ color: '#e74c3c' }} prefix={<ArrowUpOutlined />} /></Card></Col>
        <Col span={6}><Card><Statistic title="下跌" value={losers} suffix="只" valueStyle={{ color: '#27ae60' }} prefix={<ArrowDownOutlined />} /></Card></Col>
        <Col span={6}><Card><Statistic title="平盘" value={stocks.length - gainers - losers} suffix="只" valueStyle={{ color: '#999' }} prefix={<MinusCircleOutlined />} /></Card></Col>
      </Row>

      <Card
        size="small" style={{ marginTop: 12 }}
        title={<Space><StockOutlined /> 自选股行情</Space>}
        extra={
          <Space>
            <Input size="small" prefix={<SearchOutlined />} placeholder="搜索代码/名称" value={search}
              onChange={e => setSearch(e.target.value)} style={{ width: 170 }} allowClear />
            <Button size="small" icon={<PlusOutlined />} onClick={() => setAddModal({ open: true, code: '', name: '' })}>添加股票</Button>
            <Button size="small" onClick={async () => { const r = await watchlistApi.syncFromPositions(); if (r.success) { message.success(`已添加 ${r.data.added} 只`); loadData(); } else message.error(r.message) }}>从持仓添加</Button>
            <Button size="small" icon={<ReloadOutlined />} onClick={handleSync} loading={syncing}>批量同步</Button>
            <Button size="small" icon={<ReloadOutlined />} onClick={loadData}>刷新</Button>
          </Space>
        }
      >
        <div style={{ marginBottom: 8, fontSize: 13 }}>
          <Tag color={isTrading ? 'green' : 'default'}>{tradeStatus}</Tag>
          <span style={{ color: amOpen ? '#52c41a' : '#999', marginRight: 12 }}>早盘 09:30 - 11:30</span>
          <span style={{ color: pmOpen ? '#52c41a' : '#999' }}>午盘 13:00 - 15:00</span>
        </div>
        {(() => {
          const kw = search.toLowerCase();
          const ds = kw ? stocks.filter(s => s.stock_code?.toLowerCase().includes(kw) || s.stock_name?.toLowerCase().includes(kw)) : stocks;
          return <Table dataSource={ds} columns={columns} rowKey="id" size="small" loading={loading}
            pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
            locale={{ emptyText: '暂无自选股，点击"添加股票"录入' }} />;
        })()}
      </Card>

      <Modal title={<Space><span>{klineModal.stockName}({klineModal.stockCode})</span></Space>}
        open={klineModal.open} footer={null} width={850}
        onCancel={() => setKlineModal({ open: false, stockCode: '', stockName: '', data: [], loading: false })}>
        <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Tabs activeKey={String(klineScale)} onChange={v => openKLine(klineModal.stockCode, klineModal.stockName, parseInt(v))}
            items={scaleOptions.map(s => ({ key: String(s.value), label: s.label }))} size="small" style={{ margin: 0 }} />
          <Button size="small" type={showMacd ? 'primary' : 'default'} onClick={() => setShowMacd(!showMacd)}>
            MACD {showMacd ? 'ON' : 'OFF'}
          </Button>
        </div>
        {klineModal.loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#999' }}>加载中...</div>
        ) : klineModal.data.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#999' }}>暂无K线数据</div>
        ) : (
          <div style={{ background: '#1a1a2e', borderRadius: 4, padding: 4 }}>
            <ReactEChartsCore option={klineOption} style={{ height: 420 }} />
          </div>
        )}
      </Modal>

      <Modal title="添加自选股" open={addModal.open}
        onOk={handleAdd} onCancel={() => setAddModal({ open: false, code: '', name: '' })}
        okText="确认" cancelText="取消">
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <div>
            <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>股票代码</div>
            <Input placeholder="输入6位股票代码" value={addModal.code}
              onChange={e => setAddModal({ ...addModal, code: e.target.value })} maxLength={6} />
          </div>
          <div>
            <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>股票名称（可选）</div>
            <Input placeholder="输入股票名称" value={addModal.name}
              onChange={e => setAddModal({ ...addModal, name: e.target.value })} />
          </div>
        </Space>
      </Modal>
    </div>
  );
}
